import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPatch, apiDelete, getAuthHeaders } from '../../lib/api'
import { 
  ArrowLeft, 
  Edit2, 
  Trash2, 
  Clock,
  Calendar,
  FileText,
  BookOpen,
  Save,
  X,
  MoreVertical,
  Loader2,
  ChevronDown,
  ChevronRight
} from 'lucide-react'

interface AllocationEntry {
  userProfessionId: string
  categoryId: string
  hoursAllocated: string
}

interface CEAllocation {
  id: string
  continuingEducationId: string
  userProfessionId: string
  categoryId: string
  hoursAllocated: string
  categoryName?: string | null
  professionName?: string | null
}

interface CERecord {
  id: string
  title: string
  provider: string | null
  hoursAwarded: string | number | null
  dateCompleted: string | null
  documentUri: string | null
  source: string
  notes: string | null
  createdAt: string
  updatedAt: string
  allocations?: CEAllocation[]
}

interface ProfessionCategory {
  id: string
  name: string
  categoryType: string
  requiredHours: number
  hoursEarned: number
}

interface ProfessionWithCategories {
  userProfessionId: string
  professionName: string
  state: string
  categories: ProfessionCategory[]
}

function isPdfUri(uri: string | null): boolean {
  if (!uri) return false
  return uri.toLowerCase().endsWith('.pdf')
}

export default function CEDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showMenu, setShowMenu] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [documentBlobUrl, setDocumentBlobUrl] = useState<string | null>(null)
  const [documentDirectUrl, setDocumentDirectUrl] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(false)

  const [editTitle, setEditTitle] = useState('')
  const [editProvider, setEditProvider] = useState('')
  const [editHours, setEditHours] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editAllocations, setEditAllocations] = useState<AllocationEntry[]>([])
  const [expandedProfessions, setExpandedProfessions] = useState<Set<string>>(new Set())

  const { data: ceRecord, isLoading, error } = useQuery({
    queryKey: ['/api/continuing-education', id],
    queryFn: () => apiGet<CERecord>(`/continuing-education/${id}`),
    enabled: !!id,
  })

  const { data: professionsWithCategories } = useQuery({
    queryKey: ['/api/user/professions-with-categories'],
    queryFn: () => apiGet<ProfessionWithCategories[]>('/user/professions-with-categories'),
    enabled: isEditing,
  })

  const updateMutation = useMutation({
    mutationFn: async (data: { title?: string; provider?: string; hoursAwarded?: string; notes?: string; allocations?: AllocationEntry[] }) => {
      const response = await fetch(`/api/continuing-education/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errBody = await response.text()
        console.error('CE update failed:', response.status, errBody)
        throw new Error(`Update failed (${response.status}): ${errBody}`)
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/continuing-education'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/ce-progress-summary'] })
      setIsEditing(false)
    },
    onError: (error: any) => {
      console.error('CE update mutation error:', error)
      window.alert(error?.message || 'Failed to update CE record')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/continuing-education/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/continuing-education'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/ce-progress-summary'] })
      navigate('/ce')
    },
  })

  const isPdf = isPdfUri(ceRecord?.documentUri ?? null)

  useEffect(() => {
    if (ceRecord?.documentUri) {
      setImageLoading(true)
      const baseUrl = window.location.origin

      const getFileKey = (): string => {
        if (ceRecord.documentUri!.startsWith('/api/files/')) {
          return decodeURIComponent(ceRecord.documentUri!.replace('/api/files/', ''))
        }
        return ceRecord.documentUri!
      }

      const getSignedUrl = (key: string): Promise<string> => {
        const urlEndpoint = `${baseUrl}/api/files/${encodeURIComponent(key)}/url`
        return fetch(urlEndpoint, {
          headers: getAuthHeaders(),
          credentials: 'include',
        })
          .then(res => res.json())
          .then(data => {
            if (data.url) return data.url
            throw new Error('No URL returned')
          })
      }

      if (ceRecord.documentUri!.startsWith('http')) {
        if (isPdf) {
          setDocumentDirectUrl(ceRecord.documentUri!)
        } else {
          setDocumentBlobUrl(ceRecord.documentUri!)
        }
        setImageLoading(false)
      } else if (isPdf) {
        const key = getFileKey()
        getSignedUrl(key)
          .then(url => {
            setDocumentDirectUrl(url)
            setImageLoading(false)
          })
          .catch(err => {
            console.error('Failed to load PDF URL:', err)
            setImageLoading(false)
          })
      } else {
        const key = getFileKey()
        const proxyUrl = `${baseUrl}/api/files/${encodeURIComponent(key)}`
        fetch(proxyUrl, {
          headers: getAuthHeaders(),
          credentials: 'include',
        })
          .then(res => {
            if (!res.ok) throw new Error('Failed to load image')
            return res.blob()
          })
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob)
            setDocumentBlobUrl(blobUrl)
          })
          .catch(err => {
            console.error('Failed to load document:', err)
          })
          .finally(() => setImageLoading(false))
      }
    }
    return () => {
      if (documentBlobUrl) {
        URL.revokeObjectURL(documentBlobUrl)
      }
    }
  }, [ceRecord?.documentUri])

  const startEditing = () => {
    if (!ceRecord) return
    setEditTitle(ceRecord.title || '')
    setEditProvider(ceRecord.provider || '')
    setEditHours(String(ceRecord.hoursAwarded || ''))
    setEditNotes(ceRecord.notes || '')
    const existingAllocations: AllocationEntry[] = (ceRecord.allocations || []).map(a => ({
      userProfessionId: a.userProfessionId,
      categoryId: a.categoryId,
      hoursAllocated: a.hoursAllocated,
    }))
    setEditAllocations(existingAllocations)
    const profIds = new Set(existingAllocations.map(a => a.userProfessionId))
    setExpandedProfessions(profIds)
    setIsEditing(true)
    setShowMenu(false)
  }

  const maxHours = parseFloat(editHours) || 0

  const getProfessionTotal = (profId: string) => {
    return editAllocations
      .filter(a => a.userProfessionId === profId)
      .reduce((sum, a) => sum + (parseFloat(a.hoursAllocated) || 0), 0)
  }

  const getProfessionRemaining = (profId: string) => {
    return Math.max(0, maxHours - getProfessionTotal(profId))
  }

  const toggleCategory = (userProfessionId: string, categoryId: string) => {
    setEditAllocations(prev => {
      const exists = prev.find(a => a.userProfessionId === userProfessionId && a.categoryId === categoryId)
      if (exists) {
        return prev.filter(a => !(a.userProfessionId === userProfessionId && a.categoryId === categoryId))
      }
      const profTotal = prev
        .filter(a => a.userProfessionId === userProfessionId)
        .reduce((sum, a) => sum + (parseFloat(a.hoursAllocated) || 0), 0)
      const remaining = Math.max(0, maxHours - profTotal)
      const defaultHrs = remaining > 0 ? Math.min(remaining, maxHours).toString() : maxHours.toString()
      return [...prev, { userProfessionId, categoryId, hoursAllocated: defaultHrs }]
    })
  }

  const updateAllocationHours = (userProfessionId: string, categoryId: string, hours: string) => {
    const cleaned = hours.replace(/[^0-9.]/g, '')
    setEditAllocations(prev =>
      prev.map(a =>
        a.userProfessionId === userProfessionId && a.categoryId === categoryId
          ? { ...a, hoursAllocated: cleaned }
          : a
      )
    )
  }

  const toggleProfession = (profId: string) => {
    setExpandedProfessions(prev => {
      const next = new Set(prev)
      if (next.has(profId)) {
        next.delete(profId)
      } else {
        next.add(profId)
      }
      return next
    })
  }

  const handleSave = () => {
    if (maxHours > 0 && professionsWithCategories) {
      const overAllocatedProfs = professionsWithCategories.filter(prof => {
        const profTotal = getProfessionTotal(prof.userProfessionId)
        return profTotal > maxHours
      })
      if (overAllocatedProfs.length > 0) {
        const names = overAllocatedProfs.map(p => p.professionName).join(', ')
        window.alert(`Hours exceed the CE total (${maxHours}) for: ${names}. Please adjust your allocations.`)
        return
      }
    }
    updateMutation.mutate({
      title: editTitle,
      provider: editProvider,
      hoursAwarded: editHours,
      notes: editNotes,
      allocations: editAllocations,
    })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not specified'
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'ai_extracted':
        return 'Uploaded Certificate'
      case 'manual':
        return 'Manual Entry'
      default:
        return source
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !ceRecord) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">CE record not found</h3>
        <button
          onClick={() => navigate('/ce')}
          className="text-blue-600 hover:underline"
        >
          Back to CE Records
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/ce')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        
        {!isEditing && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <MoreVertical className="w-5 h-5 text-gray-600" />
            </button>
            
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    onClick={startEditing}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      setShowDeleteConfirm(true)
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-6">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <input
                  type="text"
                  value={editProvider}
                  onChange={(e) => setEditProvider(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                <input
                  type="number"
                  step="0.25"
                  value={editHours}
                  onChange={(e) => setEditHours(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes..."
                />
              </div>
              {professionsWithCategories && professionsWithCategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apply to Categories</label>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {professionsWithCategories.map(prof => (
                      <div key={prof.userProfessionId}>
                        <button
                          type="button"
                          onClick={() => toggleProfession(prof.userProfessionId)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                        >
                          <div>
                            <span className="font-medium text-gray-900">{prof.professionName}</span>
                            <span className="text-sm text-gray-500 ml-2">({prof.state})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {editAllocations.filter(a => a.userProfessionId === prof.userProfessionId).length > 0 && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                getProfessionTotal(prof.userProfessionId) > maxHours && maxHours > 0
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {getProfessionTotal(prof.userProfessionId)}/{maxHours} hrs
                              </span>
                            )}
                            {expandedProfessions.has(prof.userProfessionId) ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </button>
                        {expandedProfessions.has(prof.userProfessionId) && prof.categories.length > 0 && (
                          <div className="px-4 pb-3 space-y-2">
                            {prof.categories.map(cat => {
                              const isSelected = editAllocations.some(
                                a => a.userProfessionId === prof.userProfessionId && a.categoryId === cat.id
                              )
                              const alloc = editAllocations.find(
                                a => a.userProfessionId === prof.userProfessionId && a.categoryId === cat.id
                              )
                              return (
                                <div key={cat.id}>
                                  <label
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleCategory(prof.userProfessionId, cat.id)}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="flex-1">
                                      <span className="text-sm text-gray-700">{cat.name}</span>
                                      <span className="text-xs text-gray-400 ml-2">
                                        ({cat.hoursEarned}/{cat.requiredHours} hrs)
                                      </span>
                                    </div>
                                  </label>
                                  {isSelected && alloc && (
                                    <div className="flex items-center gap-2 ml-10 mt-1 mb-1">
                                      <span className="text-xs text-gray-500">Hours:</span>
                                      <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max={maxHours}
                                        value={alloc.hoursAllocated}
                                        onChange={e => updateAllocationHours(prof.userProfessionId, cat.id, e.target.value)}
                                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                      />
                                      <span className="text-xs text-gray-400">of {maxHours} hrs</span>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                        {expandedProfessions.has(prof.userProfessionId) && prof.categories.length === 0 && (
                          <div className="px-4 pb-3">
                            <p className="text-sm text-gray-400">No hour-based categories</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {editAllocations.length > 0 && professionsWithCategories && (
                    <div className="mt-3 space-y-1">
                      {professionsWithCategories.map(prof => {
                        const profAllocCount = editAllocations.filter(a => a.userProfessionId === prof.userProfessionId).length
                        if (profAllocCount === 0) return null
                        const profTotal = getProfessionTotal(prof.userProfessionId)
                        const profRemaining = getProfessionRemaining(prof.userProfessionId)
                        const isOver = profTotal > maxHours && maxHours > 0
                        return (
                          <div
                            key={prof.userProfessionId}
                            className={`px-3 py-2 rounded-lg text-center text-sm font-medium ${
                              isOver ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                            }`}
                          >
                            {prof.professionName}: {profTotal} of {maxHours} hrs
                            {isOver ? (
                              <span className="text-xs text-red-500 ml-1">
                                (exceeds by {(profTotal - maxHours).toFixed(1)})
                              </span>
                            ) : profRemaining > 0 ? (
                              <span className="text-xs text-gray-500 ml-1">
                                ({profRemaining.toFixed(1)} remaining)
                              </span>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">{ceRecord.title}</h1>
                  {ceRecord.provider && (
                    <p className="text-gray-500">{ceRecord.provider}</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-100 text-blue-700 text-lg font-bold rounded-full">
                    <Clock className="w-5 h-5" />
                    {ceRecord.hoursAwarded || 0} hrs
                  </span>
                </div>
              </div>

              {ceRecord.allocations && ceRecord.allocations.length > 0 && (
                <div className="mb-4 px-3 py-2 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">
                    Applied:{' '}
                    <span className="text-gray-700 font-medium">
                      {ceRecord.allocations
                        .map(a => `${parseFloat(a.hoursAllocated).toFixed(1)} hrs ${a.categoryName || 'Unknown'}`)
                        .join(', ')}
                    </span>
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Completion Date</p>
                    <p className="font-medium text-gray-900">{formatDate(ceRecord.dateCompleted)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Source</p>
                    <p className="font-medium text-gray-900">{getSourceLabel(ceRecord.source)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Added</p>
                    <p className="font-medium text-gray-900">{formatDate(ceRecord.createdAt)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="font-medium text-gray-900">{formatDate(ceRecord.updatedAt)}</p>
                  </div>
                </div>

                {ceRecord.notes && (
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500 mb-1">Notes</p>
                    <p className="text-gray-700">{ceRecord.notes}</p>
                  </div>
                )}
              </div>

              {ceRecord.documentUri && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-3">Completion Certificate</p>
                  {imageLoading ? (
                    <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : isPdf && documentDirectUrl ? (
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <iframe
                        src={documentDirectUrl}
                        title="Completion certificate PDF"
                        className="w-full border-0"
                        style={{ height: '500px' }}
                      />
                      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                        <a
                          href={documentDirectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          <FileText className="w-4 h-4" />
                          Open PDF in new tab
                        </a>
                      </div>
                    </div>
                  ) : documentBlobUrl ? (
                    <a href={documentBlobUrl} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={documentBlobUrl} 
                        alt="Completion certificate"
                        className="max-w-full h-auto rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        style={{ maxHeight: '400px' }}
                      />
                    </a>
                  ) : (
                    <p className="text-gray-400 text-sm">Unable to load certificate</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete CE Record?</h3>
            <p className="text-gray-500 mb-6">
              This will permanently delete "{ceRecord.title}" and remove any associated hour allocations. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
